import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { bffNotificationService } from '@resort-os/api-client';
import { useAuth } from '../contexts/AuthContext';
import NotificationModal from '../components/NotificationModal';

export function useHeaderNotifications() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const wsUrl =
      (import.meta.env.VITE_BFF_API_URL || 'http://localhost:8086').replace(/^http/, 'ws') + '/ws';
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let delay = 1000;

    const connect = () => {
      if (ws?.readyState === WebSocket.OPEN) return;
      ws = new WebSocket(wsUrl);
      ws.onmessage = event => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'notification.unread_count' && msg.user_id === user?.id) {
            setUnreadCount(msg.unread_count ?? 0);
            queryClient.invalidateQueries({ queryKey: ['bff-notifications'] });
          }
        } catch {
          /* ignore malformed frames */
        }
      };
      ws.onclose = () => {
        ws = null;
        delay = Math.min(delay * 2, 30000);
        reconnectTimer = setTimeout(connect, delay);
      };
    };
    connect();

    return () => {
      clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [user?.id, queryClient]);

  useEffect(() => {
    bffNotificationService
      .getUnreadCount()
      .then(res => setUnreadCount(res.unread_count || 0))
      .catch(() => {});
  }, []);

  return {
    unreadCount,
    onNotificationsClick: () => setOpen(prev => !prev),
    notificationModal: (
      <NotificationModal
        open={open}
        onClose={() => {
          setOpen(false);
          bffNotificationService
            .getUnreadCount()
            .then(res => setUnreadCount(res.unread_count || 0))
            .catch(() => {});
        }}
      />
    ),
  };
}
